import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApplicationConfigurationService } from '@app/core/services/ApplicationConfiguration.service';
import {
  SdmfAnalysisDto,
  SdmfAnalysisUpsertRequest,
} from './sdmf-analysis.models';

/**
 * Route for the pre-hearing SDMF analysis (#1446): one analysis per scheduled proceeding under the
 * PHMS scheduled-proceedings route family (see `PhmsSdmfAnalysisEndpoints`).
 */
const SDMF_ANALYSIS_URL = (scheduledProceedingId: number): string =>
  `/api/phms/scheduled-proceedings/${encodeURIComponent(String(scheduledProceedingId))}/sdmf-analysis`;

const quietHttpOptions = {
  headers: new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  }),
  withCredentials: true,
};

/**
 * Client facade for the SDMF analysis endpoints consumed by the pre-hearing SDMF section:
 * - {@link get} → the saved analysis for a proceeding (404 when none has been saved yet).
 * - {@link upsert} → whole-form save (PUT); creates the record on first save, replaces thereafter.
 *
 * Uses an absolute API host (apiUrl), not a relative path — the dev proxy's /api rule targets a
 * stale port and 500s relative calls. Mirrors {@link HearingRecordService}.
 */
@Injectable({ providedIn: 'root' })
export class SdmfAnalysisService {
  readonly savedByProceeding = signal<
    ReadonlyMap<number, SdmfAnalysisDto | null>
  >(new Map());

  private remember(id: number, analysis: SdmfAnalysisDto | null): void {
    this.savedByProceeding.update((saved) => new Map(saved).set(id, analysis));
  }
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(ApplicationConfigurationService);

  /** The saved SDMF analysis for the proceeding; errors with 404 when none exists yet. */
  get(scheduledProceedingId: number): Observable<SdmfAnalysisDto> {
    return this.http
      .get<SdmfAnalysisDto>(
        this.appConfig.apiUrl(SDMF_ANALYSIS_URL(scheduledProceedingId)),
        quietHttpOptions,
      )
      .pipe(
        tap({
          next: (analysis) => this.remember(scheduledProceedingId, analysis),
          error: (error: { status?: number }) => {
            if (error.status === 404)
              this.remember(scheduledProceedingId, null);
          },
        }),
      );
  }

  /**
   * Whole-form save (PUT). The payload is the complete analysis — factors and narrative; the server
   * makes the call replay-safe (mutation-idempotent), returning the saved analysis for read-back.
   */
  upsert(
    scheduledProceedingId: number,
    payload: SdmfAnalysisUpsertRequest,
    commandKey?: string,
  ): Observable<SdmfAnalysisDto> {
    return this.http
      .put<SdmfAnalysisDto>(
        this.appConfig.apiUrl(SDMF_ANALYSIS_URL(scheduledProceedingId)),
        payload,
        {
          ...quietHttpOptions,
          headers: commandKey
            ? quietHttpOptions.headers.set('Idempotency-Key', commandKey)
            : quietHttpOptions.headers,
        },
      )
      .pipe(tap((analysis) => this.remember(scheduledProceedingId, analysis)));
  }
  audit(
    scheduledProceedingId: number,
    operation: 'revisions' | 'revision' | 'compare',
    parameters: Record<string, string | number>,
  ) {
    const path =
      operation === 'revision'
        ? `/audit/revisions/${parameters['revision']}`
        : `/audit/${operation}`;
    const { revision: _revision, ...params } = parameters;
    return this.http.get<unknown>(
      this.appConfig.apiUrl(SDMF_ANALYSIS_URL(scheduledProceedingId) + path),
      { ...quietHttpOptions, params },
    );
  }
  complete(
    scheduledProceedingId: number,
    expectedRevision: number,
    commandKey?: string,
  ): Observable<SdmfAnalysisDto> {
    return this.http
      .post<SdmfAnalysisDto>(
        this.appConfig.apiUrl(
          SDMF_ANALYSIS_URL(scheduledProceedingId) + '/complete',
        ),
        { expectedRevision, completionPolicyVersion: 1 },
        {
          ...quietHttpOptions,
          headers: commandKey
            ? quietHttpOptions.headers.set('Idempotency-Key', commandKey)
            : quietHttpOptions.headers,
        },
      )
      .pipe(tap((analysis) => this.remember(scheduledProceedingId, analysis)));
  }
  reopen(
    scheduledProceedingId: number,
    expectedRevision: number,
    commandKey?: string,
  ): Observable<SdmfAnalysisDto> {
    return this.http
      .post<SdmfAnalysisDto>(
        this.appConfig.apiUrl(
          SDMF_ANALYSIS_URL(scheduledProceedingId) + '/reopen',
        ),
        { expectedRevision },
        {
          ...quietHttpOptions,
          headers: commandKey
            ? quietHttpOptions.headers.set('Idempotency-Key', commandKey)
            : quietHttpOptions.headers,
        },
      )
      .pipe(tap((analysis) => this.remember(scheduledProceedingId, analysis)));
  }
}
